import React from "react";
import { Link } from "react-router-dom";

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600">
            How we collect, use, and protect your personal information
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last updated: September 2025
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              1. Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Raah Global ("we," "our," or "us") is committed to protecting your
              privacy. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you visit our
              website and use our educational consulting services for
              international university admissions, particularly in South Korea.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              2. Information We Collect
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Personal Information:
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Full name and contact information</li>
                  <li>Email address and phone number</li>
                  <li>Educational background and academic records</li>
                  <li>Passport and visa information</li>
                  <li>Language proficiency test scores (IELTS, TOEFL)</li>
                  <li>Financial information for scholarship applications</li>
                  <li>Documents uploaded for university applications</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Technical Information:
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>IP address and browser information</li>
                  <li>Device information and usage patterns</li>
                  <li>Cookies and similar tracking technologies</li>
                  <li>Website interaction and navigation data</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              3. How We Use Your Information
            </h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Processing university and scholarship applications</li>
              <li>Communicating with universities on your behalf</li>
              <li>Providing personalized educational consulting services</li>
              <li>
                Sending updates about application status and opportunities
              </li>
              <li>Improving our website and services</li>
              <li>Complying with legal and regulatory requirements</li>
              <li>Processing payments for our services</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              4. Information Sharing and Disclosure
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                We may share your information with:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>
                  <strong>Universities and Educational Institutions:</strong>{" "}
                  Your application materials and personal information as
                  required for admission processes
                </li>
                <li>
                  <strong>Government Agencies:</strong> For visa applications
                  and legal compliance
                </li>
                <li>
                  <strong>Service Providers:</strong> Third-party vendors who
                  assist in our operations (payment processors, document
                  verification services)
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law or
                  to protect our rights and safety
                </li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-blue-800 text-sm">
                  <strong>Note:</strong> We never sell your personal information
                  to third parties for marketing purposes.
                </p>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              5. Data Security
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement appropriate technical and organizational security
              measures to protect your personal information against unauthorized
              access, alteration, disclosure, or destruction.
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>SSL encryption for data transmission</li>
              <li>Secure document storage systems</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication measures</li>
              <li>Staff training on data protection protocols</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              6. Data Retention
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We retain your personal information for as long as necessary to
              provide our services and comply with legal obligations. Typically,
              we keep your information for:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mt-4">
              <li>
                Application materials: 7 years after graduation/completion
              </li>
              <li>Communication records: 5 years</li>
              <li>Payment records: As required by tax and accounting laws</li>
              <li>Website usage data: 2 years</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              7. Your Rights
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>
                <strong>Access:</strong> Request access to your personal data
              </li>
              <li>
                <strong>Correction:</strong> Request correction of inaccurate
                information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your data
                (subject to legal requirements)
              </li>
              <li>
                <strong>Portability:</strong> Request transfer of your data to
                another service
              </li>
              <li>
                <strong>Objection:</strong> Object to processing of your data
                for marketing purposes
              </li>
            </ul>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              8. Cookies and Tracking
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our website uses cookies to improve your browsing experience and
              provide personalized content. You can control cookie settings
              through your browser preferences.
            </p>
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">
                Types of cookies we use:
              </h4>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Essential cookies for website functionality</li>
                <li>Analytics cookies to understand user behavior</li>
                <li>Preference cookies to remember your settings</li>
              </ul>
            </div>
          </section>

          {/* International Transfers */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              9. International Data Transfers
            </h2>
            <p className="text-gray-700 leading-relaxed">
              As we facilitate international education, your information may be
              transferred to and processed in countries where our partner
              universities and service providers are located, including South
              Korea. We ensure appropriate safeguards are in place for such
              transfers.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              10. Changes to This Privacy Policy
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new Privacy Policy on
              this page and updating the "Last updated" date. We encourage you
              to review this Privacy Policy periodically.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              11. Contact Us
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our data
              practices, please contact us:
            </p>
            <div className="bg-cyan-50 border border-cyan-200 p-4 rounded-lg">
              <div className="space-y-2">
                <p className="text-gray-900">
                  <strong>Raah Global</strong>
                </p>
                <p className="text-gray-700">Email: info@raahglobalpak.kr</p>
                <p className="text-gray-700">
                  Business Registration: 815-79-00669
                </p>
                <p className="text-gray-700">
                  Company Name: 라아 글로벌 (Raah Global)
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Navigation Links */}
        <div className="mt-8 text-center space-x-4">
          <Link
            to="/terms-of-service"
            className="text-cyan-600 hover:text-cyan-700 font-medium"
          >
            Terms of Service
          </Link>
          <span className="text-gray-400">|</span>
          <Link
            to="/refund-policy"
            className="text-cyan-600 hover:text-cyan-700 font-medium"
          >
            Refund Policy
          </Link>
          <span className="text-gray-400">|</span>
          <Link
            to="/"
            className="text-cyan-600 hover:text-cyan-700 font-medium"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
